import React, { useState, useMemo } from 'react';
import { Check } from 'lucide-react';
import { CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { formatText } from '@acme/lib';
import Image from 'next/image';
import { motion } from 'framer-motion';
import { AUTO_ADVANCE_CONFIG, PROGRESS_BAR_ANIMATION } from '@acme/lib';

interface WildcardCardProps {
  /**
   * Array of image URLs to display in the grid
   */
  imageUrls?: string[];

  /**
   * Title to display below the images
   */
  title: string;

  /**
   * Optional tag name to display below the title
   */
  tagName?: string;

  /**
   * Optional category to display below the title alongside the tag name
   */
  category?: string;

  /**
   * Whether the card is currently selected
   */
  checked?: boolean;

  /**
   * Callback when selection state changes
   */
  onCheckedChange?: (checked: boolean) => void;

  /**
   * Loading state for the card
   */
  skeleton?: boolean;

  /**
   * Error state for the card
   */
  hasError?: boolean;

  /**
   * Optional CSS class names to apply to the card
   */
  className?: string;

  /**
   * Layout orientation - vertical (default) or horizontal
   */
  layout?: 'vertical' | 'horizontal';

  /**
   * Whether the card is disabled
   */
  description?: string;

  /**
   * Whether to show only a single image instead of a grid of images
   * When enabled, shows the first image as a large preview with a hover overlay
   * that displays all available images in a 2x2 grid (lg screens and above)
   * If no images are available, shows a single empty state message
   */
  showSingleImage?: boolean;

  /**
   * Optional custom actions to render as overlay buttons on the card
   * These will be positioned absolutely over the image area
   */
  actions?: React.ReactNode;

  /**
   * Optional click handler for the entire card
   */
  onClick?: () => void;

  /**
   * Whether the card is currently animating (for auto-advance feedback)
   */
  isAnimating?: boolean;

  /**
   * Animation configuration for auto-advance feedback
   */
  animationConfig?: {
    animate: any;
    transition: any;
  };
}

/**
 * WildcardCard - A component that displays a grid of sample images with an optional check icon overlay
 */
export function WildcardCard({
  imageUrls = [],
  title,
  tagName,
  category,
  checked = false,
  onCheckedChange,
  skeleton = false,
  hasError = false,
  className = '',
  layout = 'vertical',
  description = '',
  showSingleImage = false,
  actions,
  onClick,
  isAnimating = false,
  animationConfig
}: WildcardCardProps) {
  const [isHoverOpen, setIsHoverOpen] = useState(false);

  const isHorizontal = layout === 'horizontal';
  const shouldShowSingleImage = showSingleImage;

  // Determine if the card is interactive
  const isInteractive = onCheckedChange !== undefined;
  const isClickable = onClick !== undefined;

  // Check if we have metadata to display
  const hasMetadata = tagName || category;

  // Format metadata text
  const metadataText = useMemo(() => {
    if (!hasMetadata) return '';

    // Clean up tagName and category
    const cleanTagName = tagName?.replace(/([a-z])([A-Z])/g, '$1 $2');
    const cleanCategory = category?.replace(/([a-z])([A-Z])/g, '$1 $2');

    const formattedTagName = cleanTagName ? formatText(cleanTagName, 'title') : '';
    const formattedCategory = cleanCategory ? formatText(cleanCategory, 'title') : '';

    if (formattedTagName && formattedCategory) {
      return `${formattedTagName} • ${formattedCategory}`;
    }

    return formattedTagName || formattedCategory;
  }, [tagName, category, hasMetadata]);

  // Handle click events - prioritize onClick over onCheckedChange
  const handleClick = () => {
    if (onClick) {
      onClick();
    } else if (onCheckedChange) {
      onCheckedChange(!checked);
    }
  };

  // Update card props to fix grid layout issues
  const cardProps = {
    className: cn(
      // Card component default styles
      'rounded-lg border bg-card text-card-foreground shadow-sm overflow-hidden',
      // Existing styles
      'relative p-3 gap-3 w-full lg:max-w-[256px] group',
      (isInteractive || isClickable) && 'cursor-pointer hover:bg-muted/50',
      checked && 'ring-2 ring-muted-foreground/10',
      isHorizontal ? 'flex flex-row' : '',
      className
    ),
    onClick: isInteractive || isClickable ? handleClick : undefined,
    role: isInteractive || isClickable ? 'button' : undefined,
    'aria-pressed': isInteractive ? checked : undefined
  };

  // Selected check icon overlay
  const CheckOverlay = checked && (
    <div className="absolute z-[1] left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 bg-blue-500 text-white rounded-full p-3 border-2 border-white shadow-md">
      <Check size={16} />
    </div>
  );

  // Error state
  if (hasError) {
    return (
      <motion.div {...cardProps} className={cn(cardProps.className, '')}>
        <p>Error loading item</p>
      </motion.div>
    );
  }

  // Loading state
  if (skeleton) {
    return (
      <motion.div {...cardProps} className={cn(cardProps.className, '')}>
        {isHorizontal ? (
          <>
            <Skeleton className="bg-foreground/10 w-[80px] h-[80px] aspect-square flex-shrink-0" />
            <div className="flex flex-col justify-center gap-2 w-full max-w-[500px]">
              <Skeleton className="h-2 w-full" />
              <Skeleton className="h-2 w-3/4" />
            </div>
          </>
        ) : (
          <>
            <div className="grid w-full gap-[1px] aspect-square mb-3">
              <Skeleton className="w-full h-full bg-foreground/10" />
            </div>
            <div className="flex flex-col items-center gap-1">
              <Skeleton className="h-2 w-3/4" />
              <Skeleton className="h-2 w-1/2 mx-auto" />
            </div>
          </>
        )}
      </motion.div>
    );
  }

  // Image grid component
  const ImageGrid = (
    <div
      className={cn(
        'relative',
        shouldShowSingleImage
          ? 'w-full aspect-square'
          : cn(
              'grid grid-cols-2 gap-[1px] aspect-square relative auto-rows-fr',
              isHorizontal ? 'w-[80px] h-[80px]' : 'w-full'
            )
      )}
    >
      {CheckOverlay}
      {actions && <div className="absolute z-[2] top-0 right-0 left-0 bottom-0 pointer-events-none">{actions}</div>}
      {shouldShowSingleImage ? (
        imageUrls.length === 0 ? (
          // Empty state when showSingleImage is true but no images available
          <div
            className={cn(
              'bg-foreground/10 overflow-hidden aspect-square w-full h-full flex items-center justify-center'
            )}
          ></div>
        ) : imageUrls.length > 1 ? (
          <div
            className={cn('bg-foreground/10 overflow-hidden aspect-square w-full h-full relative group')}
            onMouseEnter={() => setIsHoverOpen(true)}
            onMouseLeave={() => setIsHoverOpen(false)}
          >
            {/* Single image - shown by default */}
            <Image
              src={imageUrls[0]!}
              alt={`${title} sample`}
              className={cn(
                'w-full h-full object-cover transition-opacity duration-200',
                isHoverOpen ? 'opacity-0' : 'opacity-100',
                checked && !isHoverOpen && 'opacity-50'
              )}
              height={1000}
              width={1000}
            />

            {/* Grid of images - shown on hover */}
            <div
              className={cn(
                'absolute inset-0 grid grid-cols-2 gap-[1px] transition-opacity duration-200',
                isHoverOpen ? 'opacity-100' : 'opacity-0'
              )}
            >
              {[0, 1, 2, 3].map(index => (
                <div key={`hover-image-${index}`} className="overflow-hidden aspect-square">
                  {imageUrls[index] ? (
                    <Image
                      src={imageUrls[index]}
                      alt={`${title} sample ${index + 1}`}
                      width={120}
                      height={120}
                      className={cn('w-full h-full object-cover', checked && 'opacity-50')}
                    />
                  ) : (
                    <div className="w-full h-full bg-muted/30" />
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : (
          // Single image without hover when only one image is available
          <div className={cn('bg-foreground/10 overflow-hidden aspect-square w-full h-full')}>
            <Image
              src={imageUrls[0]!}
              alt={`${title} sample`}
              className={cn('w-full h-full object-cover', checked && 'opacity-50')}
              height={1000}
              width={1000}
            />
          </div>
        )
      ) : (
        [0, 1, 2, 3].map(index => (
          <div key={`image-${index}`} className={cn('bg-foreground/10 overflow-hidden aspect-square w-full h-full')}>
            {imageUrls[index] ? (
              <Image
                src={imageUrls[index]}
                alt={`${title} sample ${index + 1}`}
                className={cn('aspect-square pointer-events-none object-cover', checked && 'opacity-50')}
                height={1000}
                width={1000}
              />
            ) : (
              <Skeleton className="w-full h-full animate-none" />
            )}
          </div>
        ))
      )}
    </div>
  );

  return (
    <motion.div
      {...cardProps}
      className={cn(cardProps.className, 'overflow-hidden')}
      animate={isAnimating && animationConfig ? animationConfig.animate : {}}
      transition={isAnimating && animationConfig ? animationConfig.transition : {}}
    >
      {/* Progress bar background */}
      {isAnimating && (
        <motion.div
          className="absolute inset-0 origin-left z-[0]"
          style={{
            backgroundColor: AUTO_ADVANCE_CONFIG.PROGRESS_BAR_COLOR,
            opacity: AUTO_ADVANCE_CONFIG.PROGRESS_BAR_OPACITY
          }}
          initial={PROGRESS_BAR_ANIMATION.initial}
          animate={PROGRESS_BAR_ANIMATION.animate}
          transition={PROGRESS_BAR_ANIMATION.transition}
        />
      )}

      <div className="relative z-[1]">
        {isHorizontal ? (
          <div className="flex flex-row items-center">
            <div className="relative flex-shrink-0">{ImageGrid}</div>
            <div className="flex-1 pl-3 flex flex-col justify-center min-w-0">
              <CardTitle className="text-lg mb-0.5 truncate">{title}</CardTitle>
              {metadataText && <p className="text-sm text-muted-foreground truncate">{metadataText}</p>}
            </div>
          </div>
        ) : (
          <div>
            <div className="relative">{ImageGrid}</div>
            <div className="mt-2.5">
              <CardTitle className="text-sm text-center line-clamp-2 mb-0.5">{formatText(title)}</CardTitle>
              {metadataText && <p className="text-xs text-center text-muted-foreground line-clamp-1">{metadataText}</p>}
              {description && (
                <p className="text-xs text-center text-muted-foreground line-clamp-1 capitalize">{description}</p>
              )}
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}
